import heroBg from "../assets/images/aw.webp";
import heroImage from "../assets/images/Aboutus.jpg";
import { useNavigate } from "react-router-dom";

const AboutHero = () => {
  const navigate = useNavigate();

  return (
    <section
      aria-label="About hero section"
      className="relative py-20 bg-white overflow-hidden"
    >
      {/* Background decoration */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-blue-50"></div>
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-100 rounded-full blur-3xl opacity-30"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-indigo-100 rounded-full blur-3xl opacity-30"></div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        
        {/* Left Column - Content */}
        <div>
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-full mb-6">
            <span className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></span>
            <span className="text-blue-700 text-sm font-medium tracking-wide">Employee Management System</span>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
            <span className="text-gray-900">Streamline Your</span>
            <br />
            <span className="text-blue-600">Workforce Management</span>
          </h1>

          <p className="text-gray-600 text-lg max-w-xl mb-8 leading-relaxed">
            At Aizops, we help businesses transform their HR operations with intelligent 
            employee management solutions — from onboarding to payroll, all in one place.
          </p>

          <div className="mt-5 sm:mt-6 md:mt-8 flex flex-wrap gap-3 sm:gap-4 justify-center lg:justify-start">
            <button
              onClick={() => navigate('/dashboard')}
              className="px-6 sm:px-7 md:px-8 py-2.5 sm:py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-all duration-300 shadow-md hover:shadow-lg active:scale-95 transform flex items-center gap-2"
            >
              <span>Get Started</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>

            <button
              onClick={() => navigate('/pricing')}
              className="px-6 sm:px-7 md:px-8 py-2.5 sm:py-3 border-2 border-blue-600 text-blue-600 hover:bg-blue-50 font-semibold rounded-lg transition-all duration-300"
            >
              See Pricing
            </button>
          </div>

          {/* Trust indicators */}
          <div className="mt-8 pt-6 border-t border-gray-200 flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-gray-500 text-xs">Employees Managed</p>
                <p className="text-gray-900 font-semibold text-sm">10,000+</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-gray-500 text-xs">Satisfaction Rate</p>
                <p className="text-gray-900 font-semibold text-sm">98%</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div>
                <p className="text-gray-500 text-xs">Active Companies</p>
                <p className="text-gray-900 font-semibold text-sm">500+</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Image */}
        <div className="flex justify-center lg:justify-end mt-5" aria-hidden="true">
          <div className="relative">
            {/* Glow effect */}
            <div className="absolute -inset-4 bg-blue-400/20 blur-2xl rounded-2xl"></div>
            
            {/* Image Container */}
            <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden">
              <img
                src={heroImage}
                alt="Employee Management Dashboard Illustration"
                loading="lazy"
                draggable="false"
                className="max-w-full h-auto rounded-2xl"
              />
              
              {/* Overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/10 to-transparent rounded-2xl"></div>
            </div>
            
            {/* Floating stat card 1 */}
            <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-3 border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Active Users</p>
                  <p className="text-lg font-bold text-gray-900">500+</p>
                  <p className="text-xs text-green-600">Companies</p>
                </div>
              </div>
            </div>

            {/* Floating stat card 2 */}
            <div className="absolute -top-6 -right-6 bg-white rounded-xl shadow-lg p-3 border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Efficiency</p>
                  <p className="text-lg font-bold text-gray-900">45%</p>
                  <p className="text-xs text-green-600">Improvement</p>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default AboutHero;